//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { MediaTypeOverlayIconType } from '@components/atoms/overlay-icon/MediaTypeOverlayIcon';
import { PROMOTED_POSTS_ELIGIBILITY_STATES } from '@instagram-account/instagram-account-promoted-posts/promoted-posts-eligibility-constants';
import Cuid from '@models/Cuid';
import FileId from '@models/FileId';
import { IMediaItemThumbnailData } from '@models/IMediaItemThumbnailData';
import InstagramBusinessAccountId from '@models/InstagramBusinessAccountId';
import InstagramHashtag from '@models/InstagramHashtag';
import InstagramUserId from '@models/InstagramUserId';
import VideoId from '@models/VideoId';

/**
 * Shared models for IG posts & stories sourced from the default user APIs
 */
interface IInstagramMediaItemUser {
  id?: InstagramBusinessAccountId | InstagramUserId;
  username?: string;
  isBusiness?: boolean;
}

export enum InstagramMediaItemMediaType {
  Carousel = 'Carousel',
  Image = 'Image',
  Video = 'Video',
}

export enum InstagramMediaItemType {
  Gallery = 'Gallery',
  Post = 'Post',
  Story = 'Story',
}

export enum InstagramMediaProductType {
  ADS = 'ADS',
  FEED = 'FEED',
  IGTV = 'IGTV',
  REELS = 'REELS',
  STORY = 'STORY',
}

/**
 * The model of an IG post from the IG dashboard gateway, sourced from NDW and Instagram Dashboard Gateway.
 */
export interface IInstagramPost {
  cuid: Cuid;
  itemId: string;
  itemType: InstagramMediaItemType;
  mediaType: InstagramMediaItemMediaType;
  metrics: IInstagramPostMetrics;
  media: IInstagramPostMedia;
  galleryMedia?: IInstagramPostMedia[];
  postedTime?: number;
  fileId?: FileId;
  videoId?: VideoId;
  networkUrl?: string;
  caption?: string;
  hashtags?: InstagramHashtag[];
  user?: IInstagramMediaItemUser;
  isCopyrightedPost?: boolean;
  videoTitle?: string;
  mediaProductType?: InstagramMediaProductType;
}

interface IInstagramPostMedia {
  type?: InstagramMediaItemMediaType;
  userSpatialTags?: IInstagramPostUserSpatialTag[];
  image?: IInstagramPostMediaContent;
  video?: IInstagramPostMediaContent;
}

interface IInstagramPostUserSpatialTag {
  user: IInstagramMediaItemUser;
  x: number;
  y: number;
}

interface IInstagramPostMediaContent {
  url: string;
  fileId?: FileId;
  videoId?: VideoId;
}

interface IInstagramPostMetrics {
  audienceEngagementScore?: number;
  commentCount: number;
  engagementRate?: number;
  engagementRateWithReach?: number;
  impressions?: number;
  likeCount: number;
  reach?: number;
  saveCount?: number;
  shareCount?: number;
  videoViewCount?: number;
  views?: number;
}

/**
 * Helper utils to determine post states for common
 * pieces of specialized UI (ex: gallery icons).
 */
export const isGalleryPost = (post: IInstagramPost) =>
  (post.galleryMedia?.length ?? 0) > 0;
export const isStoryPost = (post: IInstagramPost) =>
  post.itemType === InstagramMediaItemType.Story ||
  post.mediaProductType === InstagramMediaProductType.STORY;
export const isVideoPost = (post: IInstagramPost) =>
  post.mediaType === InstagramMediaItemMediaType.Video;
export const isReelsPost = (post: IInstagramPost) =>
  post.mediaProductType === InstagramMediaProductType.REELS;

export const isIgtvPost = (post: IInstagramPost) =>
  post.mediaProductType === InstagramMediaProductType.IGTV;

/**
 * Gets the media type overlay icon type for live (posted) items in the Feed Preview and
 * for the metadataLayer: {mediaType} prop in Media component
 */
export const getMediaOverlayIconType = (
  post: IInstagramPost,
): Exclude<MediaTypeOverlayIconType, 'story' | 'gif'> => {
  if (isGalleryPost(post)) {
    return 'gallery';
  } else if (isReelsPost(post)) {
    return 'Reels';
  } else if (isVideoPost(post) && !isStoryPost(post)) {
    return 'video';
  } else if (isIgtvPost(post)) {
    return 'IGTV';
  } else {
    return 'image';
  }
};

const GALLERY_PROMOTION_HELP_TEXT =
  'Only the first image of a Carousel post can be promoted. ';
const VIDEO_PROMOTION_HELP_TEXT = 'Video content cannot be promoted. ';
const PROMOTION_NOT_ELIGIBLE_DEFAULT_HELP_TEXT =
  'You cannot promote this post.';
const PROMOTION_RUNNING_HELP_TEXT =
  'A promotion is currently running for this post. To check its performance, or make changes, visit your '; // this cuts off mid sentence because it is the start of tooltip content that is followed by text wrapped in an anchor

/**
 * Gets the promotionHelpText for use in Instagram Engagement and Summary Promote action button
 */
export const getPromotionHelpText = (
  post: IInstagramPost,
  promotionStates: PROMOTED_POSTS_ELIGIBILITY_STATES,
) => {
  switch (promotionStates) {
    case PROMOTED_POSTS_ELIGIBILITY_STATES.PROMOTION_RUNNING: {
      return PROMOTION_RUNNING_HELP_TEXT;
    }

    case PROMOTED_POSTS_ELIGIBILITY_STATES.GALLERY: {
      return GALLERY_PROMOTION_HELP_TEXT;
    }

    case PROMOTED_POSTS_ELIGIBILITY_STATES.FETCHING: {
      return PROMOTION_NOT_ELIGIBLE_DEFAULT_HELP_TEXT;
    }

    case PROMOTED_POSTS_ELIGIBILITY_STATES.NOT_ELIGIBLE: {
      if (isIgtvPost(post) || isReelsPost(post) || isVideoPost(post)) {
        return VIDEO_PROMOTION_HELP_TEXT;
      }

      return PROMOTION_NOT_ELIGIBLE_DEFAULT_HELP_TEXT;
    }

    case PROMOTED_POSTS_ELIGIBILITY_STATES.ELIGIBLE:
      return '';
  }
};

/**
 * Instagram Story Group Types. Models representing IG Stories organized as Clips in Groups.
 */

export interface IInstagramStoryGroup {
  startTime: Date;
  endTime: Date;
  storyGroupClips: IMediaItemThumbnailData[];
  storyGroupMetrics: IInstagramStoryGroupMetrics;
}

export interface IInstagramStoryClip {
  cuid: Cuid;
  itemId: string;
  mediaUrl: string;
  itemType: InstagramMediaItemType;
  mediaType: InstagramMediaItemMediaType;
  // This field is marked optional on the backend,
  // but since we query by posted time none of the actual
  // values we get should have undefined here.
  postedDateTime: Date;
  fileId?: FileId;
  videoId?: VideoId;
  networkUrl?: string;
  thumbnailUrl?: string;
  caption?: string;
  metrics: IInstagramStoryClipMetrics;
}

export interface IInstagramStoryClipMetrics {
  impressions?: number;
  reach?: number;
  dropoffRate?: number;
  shareCount?: number;
  views?: number;
  // Until matching is improved, this field is almost always guaranteed to be undefined and should not be displayed
  swipeUps?: number;
  exitRate?: number;
  replies?: number;
  tapForwardRate?: number;
}

export interface IInstagramStoryGroupMetrics {
  completionRate?: number;
  impressions?: number;
  reach?: number;
  revenue?: number;
  shareCount?: number;
  swipeUps?: number;
  views?: number;
}
